import React, { useEffect, useRef } from 'react';

const EpicHeroBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;
    let waterDrops = [];
    let shineParticles = [];

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Water drop class (краплі води)
    class WaterDrop {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = -50;
        this.speed = Math.random() * 3 + 2;
        this.size = Math.random() * 3 + 1;
        this.opacity = Math.random() * 0.4 + 0.2;
      }

      update() {
        this.y += this.speed;
        if (this.y > canvas.height + 50) {
          this.reset();
        }
      }

      draw() {
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size * 2
        );
        gradient.addColorStop(0, `rgba(255, 255, 255, ${this.opacity})`);
        gradient.addColorStop(0.5, `rgba(200, 230, 255, ${this.opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.ellipse(this.x, this.y, this.size, this.size * 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Shine particle (блиск відполірованого авто)
    class ShineParticle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.life = Math.random() * 200 + 100;
        this.maxLife = this.life;
        this.twinkle = Math.random() * Math.PI * 2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.life -= 1;
        this.twinkle += 0.1;
        
        if (this.life <= 0) {
          this.reset();
        }
      }

      draw() {
        const opacity = (this.life / this.maxLife) * (0.5 + Math.sin(this.twinkle) * 0.3);
        
        // Diamond shape (діамант)
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(Math.PI / 4);
        
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size * 3);
        gradient.addColorStop(0, `rgba(229, 36, 36, ${opacity})`);
        gradient.addColorStop(0.5, `rgba(255, 255, 255, ${opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(-this.size, -this.size, this.size * 2, this.size * 2);
        
        ctx.restore();
      }
    }

    // Create water drops
    for (let i = 0; i < 30; i++) {
      waterDrops.push(new WaterDrop());
    }

    // Create shine particles
    for (let i = 0; i < 40; i++) {
      shineParticles.push(new ShineParticle());
    }

    // Flowing gradient mesh
    const drawGradientMesh = (time) => {
      const meshSize = 100;
      const cols = Math.ceil(canvas.width / meshSize) + 1;
      const rows = Math.ceil(canvas.height / meshSize) + 1;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * meshSize;
          const y = j * meshSize;
          
          const offset = Math.sin((i + j) * 0.5 + time * 0.001) * 20;
          const opacity = 0.01 + Math.sin(time * 0.0005 + i * 0.1) * 0.005;
          
          const gradient = ctx.createRadialGradient(
            x + offset, y + offset, 0,
            x + offset, y + offset, meshSize
          );
          
          if ((i + j) % 2 === 0) {
            gradient.addColorStop(0, `rgba(229, 36, 36, ${opacity})`);
            gradient.addColorStop(1, 'rgba(229, 36, 36, 0)');
          } else {
            gradient.addColorStop(0, `rgba(192, 192, 192, ${opacity})`);
            gradient.addColorStop(1, 'rgba(192, 192, 192, 0)');
          }
          
          ctx.fillStyle = gradient;
          ctx.fillRect(x, y, meshSize, meshSize);
        }
      }
    };

    // Shimmer effect (блиск)
    const drawShimmer = (time) => {
      const shimmerX = (time * 0.5) % (canvas.width + 200) - 100;
      
      const gradient = ctx.createLinearGradient(
        shimmerX - 50, 0,
        shimmerX + 50, 0
      );
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(shimmerX - 50, 0, 100, canvas.height);
    };

    const animate = () => {
      time += 1;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw gradient mesh
      drawGradientMesh(time);

      // Draw shimmer
      if (time % 300 < 100) {
        drawShimmer(time);
      }

      // Update and draw water drops
      waterDrops.forEach(drop => {
        drop.update();
        drop.draw();
      });

      // Update and draw shine particles
      shineParticles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.7 }}
    />
  );
};

export default EpicHeroBackground;